import { request } from './api-client';
import {
  ContactImportResponseSchema,
  ContactSummarySchema,
  RecipientTimelineSchema,
  pagedSchema,
  type ContactImportResult,
} from './schemas';
import type { z } from 'zod';

/**
 * One function per endpoint, each naming the schema its response is parsed through — the same shape
 * as `campaigns-api.ts`, so the API surface reads against the controller.
 */

const ContactPageSchema = pagedSchema(ContactSummarySchema);

export async function searchContacts(params: {
  query?: string;
  take?: number;
  skip?: number;
}): Promise<z.infer<typeof ContactPageSchema>> {
  const search = new URLSearchParams();

  if (params.query !== undefined && params.query !== '') search.set('query', params.query);
  if (params.take !== undefined) search.set('take', String(params.take));
  if (params.skip !== undefined) search.set('skip', String(params.skip));

  const suffix = search.size > 0 ? `?${search.toString()}` : '';

  return request(`/api/v1/contacts${suffix}`, ContactPageSchema);
}

/**
 * The CSV import.
 *
 * `FormData` rather than JSON, and deliberately NOT setting Content-Type: the browser has to add the
 * multipart boundary itself, and setting the header by hand produces a request the server cannot
 * parse. The consent fields are required by the server, so they are required here too rather than
 * being optional arguments somebody can forget.
 */
export async function importContacts(input: {
  file: File;
  consentSource: string;
  consentWording: string;
}): Promise<ContactImportResult> {
  const form = new FormData();

  form.append('file', input.file);
  form.append('consentSource', input.consentSource);
  form.append('consentWording', input.consentWording);

  return request('/api/v1/contacts/import', ContactImportResponseSchema, {
    method: 'POST',
    body: form,
  });
}

/**
 * One person's delivery history.
 *
 * The `before` cursor is keyset rather than an offset, matching the server: `events` is partitioned on
 * `occurred_at`, and an offset scan over a busy contact reads every partition to discard most of it.
 */
export function getRecipientTimeline(input: {
  contactId: string;
  campaignId?: string;
  before?: string;
  take?: number;
}) {
  const search = new URLSearchParams({ take: String(input.take ?? 50) });

  if (input.campaignId !== undefined) search.set('campaignId', input.campaignId);
  if (input.before !== undefined) search.set('before', input.before);

  return request(
    `/api/v1/contacts/${input.contactId}/timeline?${search.toString()}`,
    RecipientTimelineSchema,
  );
}
